import { useCallback } from "react";
import { useAppIdToken } from "../auth/useAppIdToken";
import { logActivity } from "../api/activityLog";

export type ReviewIssue = { label: string; suggestion: string };
export type FieldEvaluation = { sufficient: boolean; feedback: string };
export type ChatCheckResult = { sufficient: boolean; message: string };
export type BuildPath = "citizen" | "hybrid" | "engineer";
export type BuildPathResult = { path: BuildPath; stack: string[]; reasoning: string };
export type BuildSetupResult = {
  tool: string;
  why: string;
  steps: { title: string; detail: string }[];
  snippets: { label: string; kind: "prompt" | "code" | "command"; content: string }[];
};
export type BuildGuideResult = {
  overview: string;
  steps: { title: string; detail: string }[];
  watchOuts: string[];
};
/** The whole coordinated plan: path + stack + reasoning, the high-level
 *  approach (`guide`), and the hands-on `setup`. */
export type BuildPlanResult = BuildPathResult & {
  guide: BuildGuideResult;
  setup: BuildSetupResult;
};

async function callAssist<T>(
  idToken: string,
  body: Record<string, unknown>
): Promise<T> {
  const res = await fetch("/api/initiative-assist", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Id-Token": idToken,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(payload?.error ?? `Assistant request failed (${res.status}).`);
  }
  return res.json() as Promise<T>;
}

export function useInitiativeAssist() {
  const getIdToken = useAppIdToken();

  const review = useCallback(
    async (
      idea: string,
      fields: { label: string; value: string }[],
      strictLabels?: string[]
    ): Promise<ReviewIssue[]> => {
      const idToken = await getIdToken();
      const { issues } = await callAssist<{ issues: ReviewIssue[] }>(idToken, {
        mode: "review",
        idea,
        fields,
        strictLabels,
      });
      void logActivity(
        "Feature",
        "AIReview",
        `Ran AI review on a new initiative (${issues.length} issue${issues.length === 1 ? "" : "s"} flagged)`
      );
      return issues;
    },
    [getIdToken]
  );

  // Judges one answer at a time, conversationally — used by the
  // experimental chat submission flow (see AddInitiativeChatModal).
  const chatCheck = useCallback(
    async (
      idea: string,
      label: string,
      value: string,
      strict?: boolean
    ): Promise<ChatCheckResult> => {
      const idToken = await getIdToken();
      return callAssist<ChatCheckResult>(idToken, { mode: "chat", idea, label, value, strict });
    },
    [getIdToken]
  );

  // One coordinated plan for an initiative — build path, stack, approach and
  // hands-on setup — from a single model call so the pieces always agree.
  // Used by the Admin "Next up" list, on demand per item.
  const buildPlan = useCallback(
    async (initiative: { label: string; value: string }[]): Promise<BuildPlanResult> => {
      const idToken = await getIdToken();
      const result = await callAssist<BuildPlanResult>(idToken, {
        mode: "buildPlan",
        initiative,
      });
      const title = initiative.find((f) => f.label.toLowerCase() === "title")?.value;
      void logActivity(
        "Feature",
        "BuildPlan",
        `Built a plan for "${title ?? "an initiative"}" → ${result.path} (${result.setup.tool || "no tool"})`
      );
      return result;
    },
    [getIdToken]
  );

  return { review, chatCheck, buildPlan };
}
